import { ChevronDown, Clock, Zap } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { trackUmamiEvent } from '@/helpers/analytics';
import { cn } from '@/helpers/utils';
import { GameweekStatus, type GameweekWithStatus } from '@/hooks/data/gameweekStatuses';

interface GameweekHeaderProps {
  groupedGameweeks: Record<GameweekStatus, GameweekWithStatus[]>;
  selectedGameweek: GameweekWithStatus | null;
  setSelectedGameweek: (gw: GameweekWithStatus) => void;
  isOffSeason?: boolean;
}

export function GameweekHeader({
  groupedGameweeks,
  selectedGameweek,
  setSelectedGameweek,
  isOffSeason = false,
}: GameweekHeaderProps) {
  const [isPreviousOpen, setIsPreviousOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState('');
  const [bonusMultiplier, setBonusMultiplier] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const nextGws = groupedGameweeks?.[GameweekStatus.UPCOMING] || [];
  const nextOne = nextGws.find((gw) => gw.is_next);
  const currentGws = groupedGameweeks?.[GameweekStatus.CURRENT] || [];
  const currentOne = currentGws[0];
  const prevGws = groupedGameweeks?.[GameweekStatus.CLOSED] || [];

  // Countdown timer for next gameweek
  useEffect(() => {
    if (!nextOne?.deadline_time) {
      setTimeLeft('');
      setBonusMultiplier(null);
      return;
    }
    const deadline = new Date(nextOne.deadline_time);
    const ONE_DAY_MS = 86400000;
    const update = () => {
      const now = new Date();
      const diff = deadline.getTime() - now.getTime();
      if (diff <= 0) {
        setTimeLeft('');
        setBonusMultiplier(null);
        return;
      }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setTimeLeft(d > 0 ? `${d}d ${h}h ${m}m` : h > 0 ? `${h}h ${m}m` : `${m}m`);
      if (diff >= 5 * ONE_DAY_MS) setBonusMultiplier('×1.15');
      else if (diff >= 3 * ONE_DAY_MS) setBonusMultiplier('×1.10');
      else if (diff >= 1 * ONE_DAY_MS) setBonusMultiplier('×1.05');
      else setBonusMultiplier(null);
    };
    update();
    const id = setInterval(update, 60000);
    return () => clearInterval(id);
  }, [nextOne]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsPreviousOpen(false);
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsPreviousOpen(false);
    };
    if (isPreviousOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEsc);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isPreviousOpen]);

  const formatDeadline = (deadline: string) => {
    const date = new Date(deadline);
    return date.toLocaleDateString([], { weekday: 'short', day: '2-digit', month: '2-digit' }) + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  return (
    <div className="space-y-2">
      {isOffSeason && (
        <p className="text-xs text-muted-foreground">
          Season hasn&apos;t started yet. Showing the last available gameweek.
        </p>
      )}
      <div className="flex flex-col md:flex-row gap-2">
      {/* Next and Current Row - always together */}
      <div className="flex gap-2 md:contents">
        {/* Next Gameweek Button */}
        {nextOne && (
          <button
            onClick={() => {
              trackUmamiEvent('duels_gameweek_select', { gameweekId: nextOne.id, type: 'next' });
              setSelectedGameweek(nextOne);
            }}
            className={cn(
              'flex-1 rounded-xl border-2 px-3 py-2 sm:px-4 sm:py-3 transition-all hover:shadow-md',
              selectedGameweek?.id === nextOne.id
                ? 'border-green-500 bg-green-50 dark:bg-green-950/30'
                : 'border-green-200 dark:border-green-900 bg-card hover:border-green-400',
            )}
          >
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
              <div className="flex flex-col items-start text-left min-w-0">
                <span className="text-sm font-semibold text-foreground">Next • {nextOne.name.replace('Gameweek ', 'GW')}</span>
                {nextOne.deadline_time && (
                  <span className="text-xs text-muted-foreground mt-0.5 hidden sm:block">{formatDeadline(nextOne.deadline_time)}</span>
                )}
              </div>
              {timeLeft && (
                <div className="flex items-center gap-1 text-green-600 dark:text-green-400 shrink-0 md:flex-col md:items-end md:gap-0.5 lg:flex-row lg:items-center lg:gap-1">
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="text-xs sm:text-sm font-semibold whitespace-nowrap">{timeLeft}</span>
                  </div>
                  {bonusMultiplier && (
                    <>
                      <span className="text-muted-foreground mx-0.5 hidden lg:inline">·</span>
                      <span className="inline-flex items-center gap-0.5 text-amber-500 dark:text-amber-400 cursor-default">
                        <Zap className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="text-xs sm:text-sm font-semibold whitespace-nowrap">{bonusMultiplier}</span>
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
          </button>
        )}

        {/* Current/Live Gameweek Button */}
        {currentOne && (
          <button
            onClick={() => {
              trackUmamiEvent('duels_gameweek_select', { gameweekId: currentOne.id, type: 'current' });
              setSelectedGameweek(currentOne);
            }}
            className={cn(
              'flex-1 rounded-xl border-2 px-3 py-2 sm:px-4 sm:py-3 transition-all hover:shadow-md',
              selectedGameweek?.id === currentOne.id
                ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/30'
                : 'border-amber-200 dark:border-amber-900 bg-card hover:border-amber-400',
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex flex-col items-start text-left min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-semibold text-foreground">Current • {currentOne.name.replace('Gameweek ', 'GW')}</span>
                  <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse shrink-0" />
                </div>
                {currentOne.deadline_time && (
                  <span className="text-xs text-muted-foreground mt-0.5 hidden sm:block">{formatDeadline(currentOne.deadline_time)}</span>
                )}
              </div>
            </div>
          </button>
        )}
      </div>

      {/* Previous Gameweeks Dropdown */}
      {prevGws.length > 0 && (
        <div className="relative flex-1" ref={dropdownRef}>
          <button
            onClick={() => setIsPreviousOpen(!isPreviousOpen)}
            className={cn(
              'w-full h-full rounded-xl border-2 px-3 py-2 sm:px-4 sm:py-3 transition-all hover:shadow-md',
              prevGws.some((gw) => gw.id === selectedGameweek?.id)
                ? 'border-primary bg-primary/5'
                : 'border-border bg-card hover:border-primary/50',
            )}
          >
            <div className="flex items-center justify-between gap-2 h-full">
              <div className="flex flex-col items-start text-left min-w-0 justify-center">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-foreground">
                    {prevGws.some((gw) => gw.id === selectedGameweek?.id)
                      ? selectedGameweek?.name.replace('Gameweek ', 'GW')
                      : 'Previous GWs'}
                  </span>
                  {!prevGws.some((gw) => gw.id === selectedGameweek?.id) && (
                    <ChevronDown className={cn('h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground transition-transform', isPreviousOpen && 'rotate-180')} />
                  )}
                </div>
                {prevGws.some((gw) => gw.id === selectedGameweek?.id) && selectedGameweek?.deadline_time && (
                  <span className="text-xs text-muted-foreground mt-0.5 hidden sm:block">Finished • {formatDeadline(selectedGameweek.deadline_time)}</span>
                )}
              </div>
              {prevGws.some((gw) => gw.id === selectedGameweek?.id) ? (
                <ChevronDown className={cn('h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground transition-transform shrink-0', isPreviousOpen && 'rotate-180')} />
              ) : (
                <span className="px-1.5 py-0.5 sm:px-2 rounded-full bg-muted text-muted-foreground text-xs font-medium shrink-0">
                  {prevGws.length} {prevGws.length === 1 ? 'GW' : 'GWs'}
                </span>
              )}
            </div>
          </button>

          {/* Previous Gameweeks Dropdown Menu */}
          {isPreviousOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 rounded-xl border bg-popover shadow-lg overflow-hidden z-50">
              <div className="px-3 pt-3 pb-2 text-xs font-semibold text-muted-foreground uppercase">Previous Gameweeks</div>
              <div className="max-h-80 overflow-y-auto">
                {prevGws.slice().reverse().slice(0, 20).map((gw) => (
                  <button
                    key={gw.id}
                    onClick={() => {
                      trackUmamiEvent('duels_gameweek_select', { gameweekId: gw.id, type: 'previous' });
                      setSelectedGameweek(gw);
                      setIsPreviousOpen(false);
                    }}
                    className={cn(
                      'w-full flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors border-t text-left',
                      selectedGameweek?.id === gw.id && 'bg-primary/10 border-l-2 border-l-primary',
                    )}
                  >
                    <div className="flex flex-col items-start">
                      <span className="font-semibold text-sm">{gw.name.replace('Gameweek ', 'GW')}</span>
                      {gw.deadline_time && (
                        <span className="text-xs text-muted-foreground">Finished • {formatDeadline(gw.deadline_time)}</span>
                      )}
                    </div>
                    {selectedGameweek?.id === gw.id && (
                      <span className="h-2 w-2 rounded-full bg-primary" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}

